import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, useNavigate, NavLink } from 'react-router-dom';
import axios from 'axios';
import { LayoutDashboard, Package, FolderOpen, FileText, MessageSquare, LogOut, Plus, Edit, Trash2, Save, Play, Upload, Star, GripVertical, X, Image, Video, Mail, Phone, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

// Dynamically determine API base URL
const getApiBase = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  if (envUrl && envUrl.trim() !== '') {
    return `${envUrl}/api`;
  }
  return '/api';
};

const API = getApiBase();

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmin(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('admin_token');
      navigate('/admin/login');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Uitgelogd');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="admin-sidebar" data-testid="admin-sidebar">
        <div className="flex items-center space-x-3 mb-8">
          <img src="/logo.png" alt="Vanden Heede Logo" className="h-10 w-auto" />
          <span className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Poppins' }}>CMS</span>
        </div>

        <nav className="space-y-1">
          <NavLink
            to="/admin/dashboard"
            className={({ isActive }) =>
              `flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${isActive ? 'bg-red-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`
            }
            data-testid="nav-dashboard"
          >
            <LayoutDashboard size={20} />
            <span>Dashboard</span>
          </NavLink>

          <NavLink
            to="/admin/services"
            className={({ isActive }) =>
              `flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${isActive ? 'bg-red-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`
            }
            data-testid="nav-services"
          >
            <Package size={20} />
            <span>Diensten</span>
          </NavLink>

          <NavLink
            to="/admin/projects"
            className={({ isActive }) =>
              `flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${isActive ? 'bg-red-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`
            }
            data-testid="nav-projects"
          >
            <FolderOpen size={20} />
            <span>Projecten</span>
          </NavLink>

          <NavLink
            to="/admin/content"
            className={({ isActive }) =>
              `flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${isActive ? 'bg-red-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`
            }
            data-testid="nav-content"
          >
            <FileText size={20} />
            <span>Site Content</span>
          </NavLink>

          <NavLink
            to="/admin/messages"
            className={({ isActive }) =>
              `flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${isActive ? 'bg-red-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`
            }
            data-testid="nav-messages"
          >
            <MessageSquare size={20} />
            <span>Berichten</span>
          </NavLink>
        </nav>

        <div className="mt-auto pt-8 border-t border-gray-200">
          <div className="text-sm text-gray-500 mb-4">
            <p className="font-semibold text-gray-900 mb-1">Ingelogd als:</p>
            <p className="truncate">{admin?.name}</p>
            <p className="truncate text-xs">{admin?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-100 w-full transition-colors"
            data-testid="logout-btn"
          >
            <LogOut size={20} />
            <span>Uitloggen</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-content" data-testid="admin-content">
        <Routes>
          <Route index element={<DashboardHome admin={admin} />} />
          <Route path="dashboard" element={<DashboardHome admin={admin} />} />
          <Route path="services" element={<ServicesManager />} />
          <Route path="projects" element={<ProjectsManager />} />
          <Route path="content" element={<ContentManager />} />
          <Route path="messages" element={<MessagesManager />} />
        </Routes>
      </main>
    </div>
  );
};

// Dashboard Home Component
const DashboardHome = ({ admin }) => {
  const [stats, setStats] = useState({ services: 0, projects: 0, messages: 0 });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const [servicesRes, projectsRes, messagesRes] = await Promise.all([
        axios.get(`${API}/services`),
        axios.get(`${API}/projects`),
        axios.get(`${API}/contact`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setStats({
        services: servicesRes.data.length,
        projects: projectsRes.data.length,
        messages: messagesRes.data.filter(m => !m.read).length
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  return (
    <div data-testid="dashboard-home">
      <h1 className="text-3xl font-bold mb-8" style={{ fontFamily: 'Poppins' }}>Dashboard</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="admin-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm mb-1">Totaal Diensten</p>
              <p className="text-3xl font-bold" data-testid="stat-services">{stats.services}</p>
            </div>
            <Package className="text-red-600" size={40} />
          </div>
        </div>

        <div className="admin-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm mb-1">Totaal Projecten</p>
              <p className="text-3xl font-bold" data-testid="stat-projects">{stats.projects}</p>
            </div>
            <FolderOpen className="text-red-600" size={40} />
          </div>
        </div>

        <div className="admin-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm mb-1">Ongelezen Berichten</p>
              <p className="text-3xl font-bold" data-testid="stat-messages">{stats.messages}</p>
            </div>
            <MessageSquare className="text-red-600" size={40} />
          </div>
        </div>
      </div>

      <div className="admin-card">
        <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Poppins' }}>Welkom, {admin?.name}!</h2>
        <p className="text-gray-600">Beheer hier de content van uw website. Gebruik het menu aan de linkerkant om te navigeren.</p>
      </div>
    </div>
  );
};

// Services Manager Component
const ServicesManager = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingService, setEditingService] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Kon diensten niet laden');
    }
  };

  const handleSave = async (serviceData) => {
    const token = localStorage.getItem('admin_token');
    try {
      if (serviceData.id) {
        await axios.put(`${API}/services/${serviceData.id}`, serviceData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Dienst bijgewerkt');
      } else {
        await axios.post(`${API}/services`, serviceData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Dienst toegevoegd');
      }
      fetchServices();
      setIsDialogOpen(false);
      setEditingService(null);
    } catch (error) {
      console.error('Error saving service:', error);
      toast.error('Fout bij opslaan');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Weet u zeker dat u deze dienst wilt verwijderen?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/services/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Dienst verwijderd');
      fetchServices();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error('Fout bij verwijderen');
    }
  };

  if (loading) return <div className="flex justify-center"><div className="loading-spinner"></div></div>;

  return (
    <div data-testid="services-manager">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Poppins' }}>Diensten Beheer</h1>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { setEditingService(null); setIsDialogOpen(true); }} className="bg-red-600 hover:bg-red-700" data-testid="add-service-btn">
              <Plus className="mr-2" size={20} /> Nieuwe Dienst
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl" data-testid="service-dialog">
            <DialogHeader>
              <DialogTitle>{editingService ? 'Dienst Bewerken' : 'Nieuwe Dienst'}</DialogTitle>
            </DialogHeader>
            <ServiceForm service={editingService} onSave={handleSave} onCancel={() => setIsDialogOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="services-list">
        {services.map((service, index) => (
          <div key={service.id} className="admin-card" data-testid={`service-item-${index}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-4">
                <div className="service-icon" style={{ width: '48px', height: '48px', fontSize: '20px' }}>
                  <i className={service.icon}></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold" style={{ fontFamily: 'Poppins' }}>{service.title}</h3>
                  <p className="text-sm text-gray-500">Volgorde: {service.order}</p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => { setEditingService(service); setIsDialogOpen(true); }}
                  className="p-2 hover:bg-gray-100 rounded transition-colors"
                  data-testid={`edit-service-btn-${index}`}
                >
                  <Edit size={18} className="text-blue-600" />
                </button>
                <button
                  onClick={() => handleDelete(service.id)}
                  className="p-2 hover:bg-gray-100 rounded transition-colors"
                  data-testid={`delete-service-btn-${index}`}
                >
                  <Trash2 size={18} className="text-red-600" />
                </button>
              </div>
            </div>
            <p className="text-gray-600 text-sm">{service.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const ServiceForm = ({ service, onSave, onCancel }) => {
  const [formData, setFormData] = useState(service || { title: '', description: '', icon: 'fas fa-hammer', order: 0 });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="service-form">
      <div>
        <label className="block text-sm font-semibold mb-2">Titel</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          className="form-input"
          required
          data-testid="service-title-input"
        />
      </div>
      <div>
        <label className="block text-sm font-semibold mb-2">Beschrijving</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="form-input"
          rows={4}
          required
          data-testid="service-description-input"
        />
      </div>
      <div>
        <label className="block text-sm font-semibold mb-2">Icon (FontAwesome class)</label>
        <input
          type="text"
          value={formData.icon}
          onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
          className="form-input"
          placeholder="fas fa-hammer"
          required
          data-testid="service-icon-input"
        />
      </div>
      <div>
        <label className="block text-sm font-semibold mb-2">Volgorde</label>
        <input
          type="number"
          value={formData.order}
          onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
          className="form-input"
          data-testid="service-order-input"
        />
      </div>
      <div className="flex justify-end space-x-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} data-testid="service-cancel-btn">Annuleren</Button>
        <Button type="submit" className="bg-red-600 hover:bg-red-700" data-testid="service-save-btn">
          <Save className="mr-2" size={18} /> Opslaan
        </Button>
      </div>
    </form>
  );
};

// Projects Manager Component
const ProjectsManager = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProject, setEditingProject] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Kon projecten niet laden');
    }
  };

  const handleSave = async (projectData) => {
    const token = localStorage.getItem('admin_token');
    try {
      if (projectData.id) {
        await axios.put(`${API}/projects/${projectData.id}`, projectData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Project bijgewerkt');
      } else {
        await axios.post(`${API}/projects`, projectData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Project toegevoegd');
      }
      fetchProjects();
      setIsDialogOpen(false);
      setEditingProject(null);
    } catch (error) {
      console.error('Error saving project:', error);
      toast.error('Fout bij opslaan');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Weet u zeker dat u dit project wilt verwijderen?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/projects/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project verwijderd');
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Fout bij verwijderen');
    }
  };

  if (loading) return <div className="flex justify-center"><div className="loading-spinner"></div></div>;

  return (
    <div data-testid="projects-manager">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Poppins' }}>Projecten Beheer</h1>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { setEditingProject(null); setIsDialogOpen(true); }} className="bg-red-600 hover:bg-red-700" data-testid="add-project-btn">
              <Plus className="mr-2" size={20} /> Nieuw Project
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="project-dialog">
            <DialogHeader>
              <DialogTitle>{editingProject ? 'Project Bewerken' : 'Nieuw Project'}</DialogTitle>
            </DialogHeader>
            <ProjectForm project={editingProject} onSave={handleSave} onCancel={() => setIsDialogOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-list">
        {projects.map((project, index) => (
          <div key={project.id} className="admin-card p-0 overflow-hidden" data-testid={`project-item-${index}`}>
            <img src={project.image_url} alt={project.title} className="w-full h-48 object-cover" />
            <div className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="text-lg font-bold" style={{ fontFamily: 'Poppins' }}>{project.title}</h3>
                  <p className="text-xs text-gray-500">{project.category}</p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => { setEditingProject(project); setIsDialogOpen(true); }}
                    className="p-2 hover:bg-gray-100 rounded transition-colors"
                    data-testid={`edit-project-btn-${index}`}
                  >
                    <Edit size={16} className="text-blue-600" />
                  </button>
                  <button
                    onClick={() => handleDelete(project.id)}
                    className="p-2 hover:bg-gray-100 rounded transition-colors"
                    data-testid={`delete-project-btn-${index}`}
                  >
                    <Trash2 size={16} className="text-red-600" />
                  </button>
                </div>
              </div>
              <p className="text-gray-600 text-sm">{project.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ProjectForm = ({ project, onSave, onCancel }) => {
  const [formData, setFormData] = useState(() => {
    const defaultData = { 
      title: '', 
      description: '', 
      category: 'nieuwbouw', 
      image_url: '',
      images: [],
      videos: [],
      order: 0 
    };
    if (project) {
      return { ...defaultData, ...project };
    }
    return defaultData;
  });
  
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const categories = [
    { id: 'nieuwbouw', label: 'Nieuwbouw & Renovatie' },
    { id: 'hellende-platte-daken', label: 'Hellende & Platte Daken' },
    { id: 'gevel-goot', label: 'Gevel & Gootbekleding' },
    { id: 'zink-koper', label: 'Zink & Koperwerken' },
    { id: 'isolatie', label: 'Isolatiewerken' }
  ];

  // Combine all media into one array for display
  const getAllMedia = useCallback(() => {
    const media = [];
    if (formData.image_url) {
      media.push({ url: formData.image_url, type: 'image', isMain: true });
    }
    (formData.images || []).forEach(url => {
      media.push({ url, type: 'image', isMain: false });
    });
    (formData.videos || []).forEach(url => {
      media.push({ url, type: 'video', isMain: false });
    });
    return media;
  }, [formData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.image_url && !formData.images?.length) {
      toast.error('Voeg minimaal één foto toe');
      return;
    }
    onSave(formData);
  };

  const uploadFile = async (file) => {
    const token = localStorage.getItem('admin_token');
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/media`, uploadFormData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    
    try {
      const uploadedMedia = [];
      
      // Upload all files first
      for (const file of files) {
        const result = await uploadFile(file);
        // Cloudinary returns full URL in result.url, use it directly
        const fullUrl = result.url;
        uploadedMedia.push({ url: fullUrl, type: result.type, name: file.name });
        toast.success(`${file.name} geüpload`);
      }
      
      // Then update state once with all uploads
      setFormData(prev => {
        let newImageUrl = prev.image_url;
        let newImages = [...(prev.images || [])];
        let newVideos = [...(prev.videos || [])];
        
        for (const media of uploadedMedia) {
          if (media.type === 'video') {
            newVideos.push(media.url);
          } else {
            // If no main image yet, set first image as main
            if (!newImageUrl) {
              newImageUrl = media.url;
            } else {
              newImages.push(media.url);
            }
          }
        }
        
        return {
          ...prev,
          image_url: newImageUrl,
          images: newImages,
          videos: newVideos
        };
      });
      
    } catch (error) {
      toast.error('Upload mislukt');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const setAsMain = (url) => {
    const currentMain = formData.image_url;
    const newImages = (formData.images || []).filter(img => img !== url);
    if (currentMain) {
      newImages.push(currentMain);
    }
    setFormData(prev => ({
      ...prev,
      image_url: url,
      images: newImages
    }));
    toast.success('Hoofdfoto gewijzigd');
  };

  const removeMedia = (url, type, isMain) => {
    if (isMain) {
      // Move first extra image to main, or clear main
      const newImages = [...(formData.images || [])];
      const newMain = newImages.shift() || '';
      setFormData(prev => ({
        ...prev,
        image_url: newMain,
        images: newImages
      }));
    } else if (type === 'video') {
      setFormData(prev => ({
        ...prev,
        videos: (prev.videos || []).filter(v => v !== url)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        images: (prev.images || []).filter(img => img !== url)
      }));
    }
  };

  const moveMedia = (fromIndex, toIndex) => {
    const allMedia = getAllMedia();
    const item = allMedia[fromIndex];
    
    // Simple reordering logic
    if (toIndex === 0 && item.type === 'image') {
      setAsMain(item.url);
    }
  };

  const isYouTube = (url) => url?.includes('youtube.com') || url?.includes('youtu.be');

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="project-form">
      {/* Basic Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold mb-2">Titel *</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="form-input"
            required
            placeholder="Projectnaam"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold mb-2">Categorie</label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            className="form-input"
          >
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold mb-2">Beschrijving</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="form-input"
          rows={3}
          placeholder="Beschrijf het project..."
        />
      </div>

      {/* Media Upload Section */}
      <div>
        <label className="block text-sm font-semibold mb-2">
          Foto's & Video's
          <span className="font-normal text-gray-500 ml-2">(Sleep om volgorde te wijzigen)</span>
        </label>
        
        {/* Upload Zone */}
        <div
          className={`upload-zone ${dragOver ? 'dragging' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*"
            className="hidden"
            onChange={(e) => handleFileSelect(e.target.files)}
          />
          {uploading ? (
            <div className="flex items-center justify-center gap-2">
              <div className="loading-spinner w-6 h-6"></div>
              <span>Uploaden...</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Upload size={32} className="text-gray-400" />
              <p className="text-gray-600">
                <span className="text-red-600 font-semibold">Klik om bestanden te kiezen</span>
                <span className="block text-sm">of sleep ze hierheen</span>
              </p>
              <p className="text-xs text-gray-400">JPG, PNG, GIF, MP4, MOV (max 50MB)</p>
            </div>
          )}
        </div>

        {/* Media Grid */}
        {getAllMedia().length > 0 && (
          <div className="media-grid">
            {getAllMedia().map((media, idx) => (
              <div 
                key={`${media.url}-${idx}`} 
                className={`media-item ${media.isMain ? 'main' : ''}`}
              >
                {media.isMain && (
                  <span className="media-item-badge">HOOFD</span>
                )}
                
                {media.type === 'video' ? (
                  isYouTube(media.url) ? (
                    <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                      <Play size={24} className="text-white" />
                    </div>
                  ) : (
                    <video src={media.url} className="w-full h-full object-cover" />
                  )
                ) : (
                  <img src={media.url} alt="" className="w-full h-full object-cover" />
                )}
                
                <div className="media-item-overlay">
                  {!media.isMain && media.type === 'image' && (
                    <button
                      type="button"
                      onClick={() => setAsMain(media.url)}
                      className="p-1.5 bg-white rounded-full hover:bg-yellow-100"
                      title="Als hoofdfoto instellen"
                    >
                      <Star size={14} className="text-yellow-600" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => removeMedia(media.url, media.type, media.isMain)}
                    className="p-1.5 bg-white rounded-full hover:bg-red-100"
                    title="Verwijderen"
                  >
                    <X size={14} className="text-red-600" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* URL inputs for external sources */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-500 mb-3">Of voeg een URL toe (bijv. YouTube):</p>
          <div className="flex gap-2">
            <input
              type="url"
              placeholder="https://www.youtube.com/watch?v=..."
              className="form-input flex-1 text-sm"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const url = e.target.value.trim();
                  if (url) {
                    if (isYouTube(url)) {
                      setFormData(prev => ({
                        ...prev,
                        videos: [...(prev.videos || []), url]
                      }));
                    } else if (url.match(/\.(mp4|mov|avi|webm)$/i)) {
                      setFormData(prev => ({
                        ...prev,
                        videos: [...(prev.videos || []), url]
                      }));
                    } else {
                      if (!formData.image_url) {
                        setFormData(prev => ({ ...prev, image_url: url }));
                      } else {
                        setFormData(prev => ({
                          ...prev,
                          images: [...(prev.images || []), url]
                        }));
                      }
                    }
                    e.target.value = '';
                    toast.success('URL toegevoegd');
                  }
                }
              }}
            />
            <Button
              type="button"
              variant="outline"
              className="text-sm"
              onClick={(e) => {
                const input = e.target.closest('.flex').querySelector('input');
                const url = input.value.trim();
                if (url) {
                  if (isYouTube(url) || url.match(/\.(mp4|mov|avi|webm)$/i)) {
                    setFormData(prev => ({
                      ...prev,
                      videos: [...(prev.videos || []), url]
                    }));
                  } else {
                    if (!formData.image_url) {
                      setFormData(prev => ({ ...prev, image_url: url }));
                    } else {
                      setFormData(prev => ({
                        ...prev,
                        images: [...(prev.images || []), url]
                      }));
                    }
                  }
                  input.value = '';
                  toast.success('URL toegevoegd');
                }
              }}
            >
              <Plus size={16} /> Toevoegen
            </Button>
          </div>
        </div>
      </div>

      {/* Order */}
      <div className="w-32">
        <label className="block text-sm font-semibold mb-2">Volgorde</label>
        <input
          type="number"
          value={formData.order}
          onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
          className="form-input"
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <Button type="button" variant="outline" onClick={onCancel}>
          Annuleren
        </Button>
        <Button type="submit" className="bg-red-600 hover:bg-red-700">
          <Save className="mr-2" size={18} /> Opslaan
        </Button>
      </div>
    </form>
  );
};

// Content Manager Component
const ContentManager = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content`);
      setContent(response.data);
      setFormData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching content:', error);
      toast.error('Kon content niet laden');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');
    try {
      await axios.put(`${API}/content`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Content bijgewerkt');
      fetchContent();
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error('Fout bij opslaan');
    }
  };

  if (loading) return <div className="flex justify-center"><div className="loading-spinner"></div></div>;

  return (
    <div data-testid="content-manager" className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-8 text-center" style={{ fontFamily: 'Poppins' }}>Site Content Beheer</h1>

      <form onSubmit={handleSave} data-testid="content-form">
        <div className="admin-card">
          <h2 className="text-xl font-bold mb-6 text-center border-b pb-4" style={{ fontFamily: 'Poppins' }}>🏠 Hero Sectie</h2>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold mb-2">Hero Titel</label>
              <input
                type="text"
                value={formData.hero_title || ''}
                onChange={(e) => setFormData({ ...formData, hero_title: e.target.value })}
                className="form-input text-center text-lg"
                data-testid="content-hero-title-input"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2">Hero Ondertitel</label>
              <textarea
                value={formData.hero_subtitle || ''}
                onChange={(e) => setFormData({ ...formData, hero_subtitle: e.target.value })}
                className="form-input text-center"
                rows={3}
                data-testid="content-hero-subtitle-input"
              />
            </div>
          </div>
        </div>

        <div className="admin-card">
          <h2 className="text-xl font-bold mb-6 text-center border-b pb-4" style={{ fontFamily: 'Poppins' }}>📍 Bedrijfsgegevens</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold mb-2">Bedrijfsnaam</label>
              <input
                type="text"
                value={formData.company_name || ''}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                className="form-input"
                data-testid="content-company-name-input"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold mb-2">Adres</label>
              <input
                type="text"
                value={formData.address || ''}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="form-input"
                data-testid="content-address-input"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2">Telefoon</label>
              <input
                type="tel"
                value={formData.phone || ''}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="form-input"
                data-testid="content-phone-input"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2">Email</label>
              <input
                type="email"
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="form-input"
                data-testid="content-email-input"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold mb-2">Over Ons Tekst</label>
              <textarea
                value={formData.about_text || ''}
                onChange={(e) => setFormData({ ...formData, about_text: e.target.value })}
                className="form-input"
                rows={4}
                data-testid="content-about-input"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-center">
          <Button type="submit" className="bg-red-600 hover:bg-red-700 px-8 py-3" data-testid="content-save-btn">
            <Save className="mr-2" size={18} /> Wijzigingen Opslaan
          </Button>
        </div>
      </form>
    </div>
  );
};

// Messages Manager Component
const MessagesManager = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await axios.get(`${API}/contact`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Kon berichten niet laden');
    }
  };

  const markAsRead = async (id) => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.patch(`${API}/contact/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchMessages();
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm('Weet u zeker dat u dit bericht wilt verwijderen?')) return;
    
    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/contact/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bericht verwijderd');
      fetchMessages();
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Fout bij verwijderen');
    }
  };

  const getAttachmentUrl = (filename) => {
    // Check if it's already a full URL (Cloudinary)
    if (filename.startsWith('http')) return filename;
    // Otherwise use relative path - works on any domain
    return `/api/uploads/${filename}`;
  };

  if (loading) return <div className="flex justify-center"><div className="loading-spinner"></div></div>;

  return (
    <div data-testid="messages-manager">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Poppins' }}>Contact Berichten</h1>
        <span className="px-4 py-2 bg-gray-100 rounded-full text-sm font-semibold">
          {messages.filter(m => !m.read).length} ongelezen / {messages.length} totaal
        </span>
      </div>

      {messages.length === 0 ? (
        <div className="admin-card text-center py-12">
          <MessageSquare className="mx-auto text-gray-400 mb-4" size={48} />
          <p className="text-gray-600">Nog geen berichten ontvangen</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="messages-list">
          {messages.map((message, index) => (
            <div 
              key={message.id} 
              className={`admin-card ${!message.read ? 'border-l-4 border-red-600' : ''}`}
              data-testid={`message-item-${index}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-bold" style={{ fontFamily: 'Poppins' }}>{message.name}</h3>
                    {!message.read && (
                      <span className="px-2 py-1 bg-red-100 text-red-600 text-xs font-semibold rounded">Nieuw</span>
                    )}
                  </div>
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <a href={`mailto:${message.email}`} className="hover:text-red-600 flex items-center">
                      <Mail size={14} className="mr-1" /> {message.email}
                    </a>
                    {message.phone && (
                      <a href={`tel:${message.phone}`} className="hover:text-red-600 flex items-center">
                        <Phone size={14} className="mr-1" /> {message.phone}
                      </a>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{new Date(message.created_at).toLocaleString('nl-BE')}</p>
                  <p className="text-sm text-gray-700 mt-1"><strong>Dienst:</strong> {message.service}</p>
                </div>
                <div className="flex items-center space-x-2">
                  {!message.read && (
                    <Button 
                      onClick={() => markAsRead(message.id)} 
                      variant="outline" 
                      size="sm"
                      className="text-green-600 border-green-600 hover:bg-green-50"
                      data-testid={`mark-read-btn-${index}`}
                    >
                      <Check size={16} className="mr-1" /> Gelezen
                    </Button>
                  )}
                  <Button 
                    onClick={() => deleteMessage(message.id)} 
                    variant="outline" 
                    size="sm"
                    className="text-red-600 border-red-600 hover:bg-red-50"
                    data-testid={`delete-message-btn-${index}`}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
              <div>
                <p className="font-semibold text-sm mb-2">Onderwerp: {message.subject}</p>
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <p className="text-gray-700 whitespace-pre-wrap">{message.message}</p>
                </div>
                
                {message.attachments && message.attachments.length > 0 && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="font-semibold text-sm mb-3 text-blue-800 flex items-center">
                      <FileText size={16} className="mr-2" /> 
                      {message.attachments.length} Bijgevoegd{message.attachments.length > 1 ? 'e bestanden' : ' bestand'}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {message.attachments.map((file, idx) => (
                        <a
                          key={idx}
                          href={getAttachmentUrl(file)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-2 p-3 bg-white rounded-lg border border-blue-200 hover:border-blue-400 hover:bg-blue-50 transition-all"
                        >
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <FileText size={20} className="text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{file}</p>
                            <p className="text-xs text-blue-600">Klik om te openen</p>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;